import { DatePicker, Form, Input, Select } from 'antd';
import type { FormInstance } from 'antd/es/form';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { PROJECT_TYPE, PROJECT_TYPE_OPTIONS } from '@/constants/project';
import { ProjectDepartmentSelector } from '@/pages/admin/projects/ProjectDepartmentSelector';
import { toManagerSelectOptions } from '@/pages/admin/projects/project-manager-options';
import { ApiError } from '@/services/api/client';
import * as projectsApi from '@/services/api/projects';
import type { CreateProjectPayload, ProjectType, UpdateProjectPayload } from '@/types/project';

export interface ProjectFormValues {
  name: string;
  projectType: ProjectType;
  summary?: string;
  dateRange?: [Dayjs, Dayjs];
  departmentIds: string[];
  projectManagerId: string;
}

interface ProjectFormFieldsProps {
  form: FormInstance<ProjectFormValues>;
  projectId?: string;
  disabled?: boolean;
}

export function ProjectFormFields({ form, projectId, disabled = false }: ProjectFormFieldsProps) {
  const departmentIds = Form.useWatch('departmentIds', form) ?? [];
  const [managerLoading, setManagerLoading] = useState(false);
  const [managerOptions, setManagerOptions] = useState<{ value: string; label: string }[]>([]);
  const prevDepartmentIdsRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (departmentIds.length === 0) {
      setManagerOptions([]);
      // 仅当用户主动清空部门时清除负责人，避免编辑回显前误清空
      if (prevDepartmentIdsRef.current && prevDepartmentIdsRef.current.length > 0) {
        form.setFieldValue('projectManagerId', undefined);
      }
      prevDepartmentIdsRef.current = departmentIds;
      return;
    }

    prevDepartmentIdsRef.current = departmentIds;
    let cancelled = false;
    setManagerLoading(true);

    const load = async () => {
      try {
        const result = projectId
          ? await projectsApi.fetchProjectManagerCandidates(projectId, departmentIds)
          : await projectsApi.fetchManagerCandidates(departmentIds);
        if (cancelled) {
          return;
        }
        const options = toManagerSelectOptions(result.items);
        setManagerOptions(options);
        const currentManagerId = form.getFieldValue('projectManagerId') as string | undefined;
        // 创建时：负责人不在候选范围则清空；编辑时保留原值，由后端校验
        if (
          !projectId
          && currentManagerId
          && !options.some((option) => option.value === currentManagerId)
        ) {
          form.setFieldValue('projectManagerId', undefined);
        }
      } catch (error) {
        if (!cancelled) {
          setManagerOptions([]);
          if (error instanceof ApiError) {
            form.setFields([{ name: 'projectManagerId', errors: [error.message] }]);
          }
        }
      } finally {
        if (!cancelled) {
          setManagerLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [departmentIds, form, projectId]);

  return (
    <>
      <Form.Item
        name="name"
        label="项目名称"
        rules={[{ required: true, message: '请输入项目名称' }, { max: 128 }]}
      >
        <Input placeholder="请输入项目名称" disabled={disabled} />
      </Form.Item>

      <Form.Item name="projectType" label="项目类型" rules={[{ required: true, message: '请选择项目类型' }]}>
        <Select options={PROJECT_TYPE_OPTIONS} placeholder="请选择项目类型" disabled={disabled} />
      </Form.Item>

      <Form.Item name="summary" label="项目简介" rules={[{ max: 1000 }]}>
        <Input.TextArea
          rows={4}
          placeholder="选填，最多 1000 字"
          showCount
          maxLength={1000}
          disabled={disabled}
        />
      </Form.Item>

      <Form.Item name="dateRange" label="项目周期">
        <DatePicker.RangePicker style={{ width: '100%' }} disabled={disabled} />
      </Form.Item>

      <Form.Item
        name="departmentIds"
        label="项目部门"
        rules={[
          {
            validator: async (_, ids: string[] | undefined) => {
              if (!ids || ids.length === 0) {
                throw new Error('请至少添加一个项目部门');
              }
            },
          },
        ]}
        extra="选择部门后，将自动选中该部门及全部下级部门，并包含其在职人员"
      >
        <ProjectDepartmentSelector disabled={disabled} />
      </Form.Item>

      <Form.Item
        name="projectManagerId"
        label="项目负责人"
        rules={[{ required: true, message: '请选择项目负责人' }]}
        extra="候选包含所选部门及下级部门的全部在职人员，部门负责人与领导优先展示"
      >
        <Select
          showSearch
          allowClear
          optionFilterProp="label"
          options={managerOptions}
          loading={managerLoading}
          disabled={disabled || departmentIds.length === 0}
          placeholder={departmentIds.length === 0 ? '请先添加项目部门' : '请选择项目负责人'}
        />
      </Form.Item>
    </>
  );
}

export function projectFormValuesFromDetail(
  project: {
    name: string;
    projectType: ProjectType;
    summary: string | null;
    startDate: string | null;
    endDate: string | null;
    projectManagerId: string;
    departments?: { id: string }[];
  },
): ProjectFormValues {
  const dateRange = parseProjectDateRange(project.startDate, project.endDate);

  return {
    name: project.name,
    projectType: project.projectType ?? PROJECT_TYPE.OTHER,
    summary: project.summary ?? undefined,
    dateRange,
    departmentIds: project.departments?.map((department) => department.id) ?? [],
    projectManagerId: project.projectManagerId,
  };
}

export function buildProjectPayload(values: ProjectFormValues): CreateProjectPayload;
export function buildProjectPayload(
  values: ProjectFormValues,
  mode: 'update',
): UpdateProjectPayload;
export function buildProjectPayload(
  values: ProjectFormValues,
  mode: 'create' | 'update' = 'create',
): CreateProjectPayload | UpdateProjectPayload {
  const start = values.dateRange?.[0];
  const end = values.dateRange?.[1];
  const hasValidRange = Boolean(start?.isValid() && end?.isValid());

  const base = {
    name: values.name.trim(),
    projectType: values.projectType,
    summary: values.summary?.trim() || undefined,
    departmentIds: values.departmentIds,
    projectManagerId: values.projectManagerId,
  };

  if (hasValidRange) {
    return {
      ...base,
      startDate: start!.format('YYYY-MM-DD'),
      endDate: end!.format('YYYY-MM-DD'),
    };
  }

  if (mode === 'update') {
    return {
      ...base,
      startDate: null,
      endDate: null,
    };
  }

  return base;
}

function parseProjectDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): [Dayjs, Dayjs] | undefined {
  if (typeof startDate !== 'string' || typeof endDate !== 'string') {
    return undefined;
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) {
    return undefined;
  }

  return [start, end];
}
